import type { AvailabilityResolver, AvailabilitySnapshot } from './availability';
import { pruneSelection } from './availability';
import type { ConfiguratorConfig } from './configurator-config';

export interface WizardState {
  pageIndex: number;
  selection: ReadonlyMap<string, number>;
  snapshot: AvailabilitySnapshot | null;
  status: 'idle' | 'loading' | 'error';
}

export interface WizardView extends WizardState {
  isFirstPage: boolean;
  isLastPage: boolean;
  /** Every group on the current page has an answer. */
  canAdvance: boolean;
  canAddToCart: boolean;
}

function attributeCodesOnPage(config: ConfiguratorConfig, pageIndex: number): string[] {
  return config.pages[pageIndex]?.groups.map(group => group.attributeCode) ?? [];
}

export function deriveView(config: ConfiguratorConfig, state: WizardState): WizardView {
  const isLastPage = state.pageIndex >= config.pages.length - 1;
  const answered = attributeCodesOnPage(config, state.pageIndex).every(code =>
    state.selection.has(code)
  );

  return {
    ...state,
    isFirstPage: state.pageIndex === 0,
    isLastPage,
    canAdvance: answered && state.status !== 'loading',
    canAddToCart: isLastPage && (state.snapshot?.isComplete ?? false) && state.status !== 'loading'
  };
}

export interface Wizard {
  getView: () => WizardView;
  select: (attributeCode: string, valueIndex: number) => Promise<void>;
  goToPage: (pageIndex: number) => void;
  next: () => void;
  previous: () => void;
  start: () => Promise<void>;
}

export function createWizard(
  config: ConfiguratorConfig,
  resolver: AvailabilityResolver,
  onChange: (view: WizardView) => void
): Wizard {
  let state: WizardState = {
    pageIndex: 0,
    selection: new Map(),
    snapshot: null,
    status: 'idle'
  };

  // a slower earlier request must not overwrite the answer to a later click
  let inFlight: AbortController | null = null;

  const emit = (): void => onChange(deriveView(config, state));

  const setState = (next: Partial<WizardState>): void => {
    state = { ...state, ...next };
    emit();
  };

  const refresh = async (selection: ReadonlyMap<string, number>): Promise<void> => {
    inFlight?.abort();
    const controller = new AbortController();
    inFlight = controller;

    setState({ selection, status: 'loading' });

    try {
      const snapshot = await resolver.get(selection, controller.signal);

      if (controller.signal.aborted) {
        return;
      }

      setState({
        snapshot,
        selection: pruneSelection(selection, snapshot),
        status: 'idle'
      });
    } catch (error) {
      if (controller.signal.aborted || (error as Error)?.name === 'AbortError') {
        return;
      }

      setState({ status: 'error' });
    }
  };

  return {
    getView: () => deriveView(config, state),
    start: () => refresh(state.selection),
    select: (attributeCode, valueIndex) => {
      const selection = new Map(state.selection);
      selection.set(attributeCode, valueIndex);
      return refresh(selection);
    },
    goToPage: pageIndex => {
      const clamped = Math.min(Math.max(pageIndex, 0), config.pages.length - 1);

      if (clamped !== state.pageIndex) {
        setState({ pageIndex: clamped });
      }
    },
    next: () => {
      if (deriveView(config, state).canAdvance) {
        setState({ pageIndex: Math.min(state.pageIndex + 1, config.pages.length - 1) });
      }
    },
    previous: () => setState({ pageIndex: Math.max(state.pageIndex - 1, 0) })
  };
}
